import apiClient from './apiClient';
import { API_ENDPOINTS, server_api } from '../utils/constants';

/**
 * Authentication Service
 * Handles all authentication related API calls
 */
class AuthService {
  /**
   * User login
   * @param {string} username - User's username/mobile number
   * @param {string} password - User's password
   * @returns {Promise} - Login response with user data and token
   */
  async login(username, password) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.LOGIN, {
        username,
        password
      });
      
      // Store token if present in response (handles both formats)
      if (response.data && response.data.token) {
        // New format: response.data.token
        this.setAuthToken(response.data.token);
      } else if (response.token) {
        // Old format: response.token
        this.setAuthToken(response.token);
      }
      
      return response;
    } catch (error) {
      throw this.handleAuthError(error);
    }
  }

  /**
   * User logout
   * @returns {Promise}
   */
  async logout() {
    try {
      // If you have a logout endpoint, call it here
      // const response = await apiClient.post(API_ENDPOINTS.LOGOUT);
      
      // Clear stored authentication data
      this.clearAuthData();
      
      return { success: true };
    } catch (error) {
      throw error;
    }
  }

  /**
   * Refresh authentication token
   * @returns {Promise}
   */
  async refreshToken() {
    try {
      const refreshToken = this.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      // Uncomment when you have refresh token endpoint
      // const response = await apiClient.post(API_ENDPOINTS.REFRESH_TOKEN, {
      //   refreshToken
      // });
      
      // if (response.token) {
      //   this.setAuthToken(response.token);
      // }
      
      // return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Forgot password
   * @param {string} email - User's email
   * @returns {Promise}
   */
  async forgotPassword(email) {
    try {
      // Uncomment when you have forgot password endpoint
      // const response = await apiClient.post(API_ENDPOINTS.FORGOT_PASSWORD, {
      //   email
      // });
      // return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Send OTP for password reset
   * @param {string} mobile - User's registered mobile number
   * @returns {Promise}
   */
  async sendPasswordResetOtp(mobile) {
    try {
      const response = await fetch(`${server_api()}/login/sendOtp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ mobile }),
      });

      const data = await response.json();

      if (!response.ok || data.Status !== 'SUCCESS') {
        throw {
          status: response.status,
          message: data?.message || 'Failed to send OTP',
          data,
        };
      }

      if (data?.data) {
        this.setAuthToken(data.data);
      }

      return data;
    } catch (error) {
      console.error('Password reset OTP error:', error);
      throw error?.message ? error : new Error('Failed to send OTP. Please try again.');
    }
  }

  /**
   * Verify OTP for password reset
   * @param {string} otp - OTP entered by the user
   * @param {string} token - Token received from send OTP API
   * @returns {Promise}
   */
  async verifyPasswordResetOtp(otp, token = this.getAuthToken()) {
    if (!token) {
      throw new Error('Missing verification token');
    }

    try {
      const response = await fetch(`${server_api()}/login/verifyOTP`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ otp, token }),
      });

      const data = await response.json();

      if (!response.ok || data.Status !== 'SUCCESS') {
        throw {
          status: response.status,
          message: data?.message || 'Failed to verify OTP',
          data,
        };
      }

      if (data?.data) {
        this.setAuthToken(data.data);
      }

      return data;
    } catch (error) {
      console.error('Password reset OTP verify error:', error);
      throw error?.message ? error : new Error('Failed to verify OTP. Please try again.');
    }
  }

  /**
   * Set new password using verification token
   * @param {string} password - New password
   * @param {string} confirmPassword - Confirm password
   * @param {string} token - Verification token from OTP verification
   * @returns {Promise}
   */
  async setNewPassword(password, confirmPassword, token = this.getAuthToken()) {
    if (!token) {
      throw new Error('Missing authorization token');
    }

    try {
      const response = await fetch(`${server_api()}/login/set_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ password, confirmPassword }),
      });

      const data = await response.json();

      if (!response.ok || data.Status !== 'SUCCESS') {
        throw {
          status: response.status,
          message: data?.message || 'Failed to reset password',
          data,
        };
      }

      return data;
    } catch (error) {
      console.error('Set new password error:', error);
      throw error?.message ? error : new Error('Failed to reset password. Please try again.');
    }
  }

  /**
   * Reset password
   * @param {string} token - Reset token
   * @param {string} newPassword - New password
   * @returns {Promise}
   */
  async resetPassword(token, newPassword) {
    try {
      // Uncomment when you have reset password endpoint
      // const response = await apiClient.post(API_ENDPOINTS.RESET_PASSWORD, {
      //   token,
      //   newPassword
      // });
      // return response;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Change password
   * @param {string} oldPassword - Current password
   * @param {string} newPassword - New password
   * @returns {Promise}
   */
  async changePassword(oldPassword, newPassword, confirmPassword) {
    const accessToken = this.getAuthToken();

    if (!accessToken) {
      throw new Error('Access token not found. Please log in again.');
    }

    try {
      const response = await fetch(`${server_api()}/login/change_password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          access_token: accessToken,
        },
        body: JSON.stringify({
          oldPassword,
          newPassword,
          confirmPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok || data?.Status === 'FAIL' || data?.status === 'FAIL') {
        throw {
          status: response.status,
          message: data?.message || data?.Message || 'Failed to change password',
          data,
        };
      }

      return data;
    } catch (error) {
      console.error('Change password error:', error);
      throw error?.message ? error : new Error('Failed to change password. Please try again.');
    }
  }

  /**
   * Change T-PIN (Transaction PIN)
   * @param {string} oldTPin - Current T-PIN
   * @param {string} newTPin - New T-PIN
   * @returns {Promise}
   */
  async changeTPin(oldTPin, newTPin) {
    try {
      const response = await apiClient.post(API_ENDPOINTS.CHANGE_TPIN, {
        oldTPin,
        newTPin
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  // Token management methods
  setAuthToken(token) {
    localStorage.setItem('authToken', token);
  }

  getAuthToken() {
    return localStorage.getItem('authToken');
  }

  setRefreshToken(token) {
    localStorage.setItem('refreshToken', token);
  }

  getRefreshToken() {
    return localStorage.getItem('refreshToken');
  }

  clearAuthData() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('UserRole');
    localStorage.removeItem('userRole');
    localStorage.removeItem('mobile');
  }

  isAuthenticated() {
    return !!this.getAuthToken();
  }

  getUserRole() {
    const storedRole = localStorage.getItem('UserRole') || localStorage.getItem('userRole');
    if (!storedRole) {
      return null;
    }

    return storedRole.trim().toLowerCase().replace(/\s+/g, '_');
  }

  setUserRole(role) {
    if (role) {
      const normalizedRole = role.trim().toLowerCase().replace(/\s+/g, '_');
      localStorage.setItem('UserRole', normalizedRole);
      localStorage.setItem('userRole', normalizedRole);
    }
  }

  // Error handling
  handleAuthError(error) {
    if (error.status === 401) {
      return {
        ...error,
        message: 'Invalid username or password'
      };
    } else if (error.status === 403) {
      return {
        ...error,
        message: 'Access denied'
      };
    } else if (error.status === 429) {
      return {
        ...error,
        message: 'Too many login attempts. Please try again later.'
      };
    }
    return error;
  }
}

// Create and export a singleton instance
const authService = new AuthService();
export default authService;

